import React, { useState, useEffect } from "react";
import styles from "./CSS/ShortsVideoContent.module.css";
import { Edit, Trash, Play, Plus, Search } from "lucide-react";
import { useShorts, useDeleteShort, useUpdateShort } from "../../../hooks/admin/useAdminShorts";
import { useNavigate } from "react-router-dom";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};

// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Play Button Component
const PlayButtonComponent = () => {
  return (
    <div className={styles.playButton}>
      <Play color="#fff" size={24} />
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel, isLoading }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>
        <div className={styles.modalButtons}>
          <button 
            className={styles.modalButton} 
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Deleting..." : "Confirm"}
          </button>
          <button 
            className={styles.modalButton} 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Short Button Component
const AddShortButton = ({ onClick }) => {
  return (
    <button className={styles.addShortButton} onClick={onClick}>
      <Plus size={20} />
      <span>Add New Short</span>
    </button>
  );
};

const ShortsVideoContent = () => {
  // State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showModal, setShowModal] = useState(false);
  const [shortToDelete, setShortToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredShorts, setFilteredShorts] = useState([]);

  // Fetch shorts data
  const { shortsData, isLoading, refetchShorts } = useShorts();
  const { deleteShort, isLoading: isDeleting } = useDeleteShort();
  const { updateShort, isLoading: isUpdating } = useUpdateShort();
  const navigate = useNavigate();

  // Filter shorts based on search term
  useEffect(() => {
    if (!shortsData) return;
    
    if (searchTerm.trim() === '') {
      setFilteredShorts(shortsData);
    } else {
      const query = searchTerm.toLowerCase();
      const filtered = shortsData.filter(short => 
        short.title.toLowerCase().includes(query)
      );
      setFilteredShorts(filtered);
    }
    
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [searchTerm, shortsData]);

  const handleEdit = (id) => {
    navigate(`/admin/shorts/edit/${id}`)
  };

  const handleHide = (id, currentlyHidden) => {
    // Update the visibility status
    updateShort({
      shortId: id,
      formData: {
        is_public: currentlyHidden // Toggle the is_public status
      }
    });
  };

  // Opens the confirmation modal instead of deleting immediately.
  const confirmDelete = (id) => {
    setShortToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion.
  const confirmDeleteAction = () => {
    if (shortToDelete !== null) {
      deleteShort(shortToDelete);
      setShowModal(false);
    }
  };

  const cancelDelete = () => {
    setShortToDelete(null);
    setShowModal(false);
  };

  const handleAddNew = () => {
    navigate('/admin/add-shorts');
  };

  // Pagination logic
  const totalItems = filteredShorts?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentShorts = filteredShorts?.slice(indexOfFirstItem, indexOfLastItem) || [];

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  if (isLoading) {
    return <div className={styles.loadingState}>Loading shorts...</div>;
  }

  return (
    <div className={styles.shortsPageContainer}>
      <div className={styles.headerSection}>
        <h1 className={styles.pageTitle}>Shorts Library</h1>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search shorts..." 
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <AddShortButton onClick={handleAddNew} />
      </div>

      {currentShorts.length === 0 ? (
        <div className={styles.emptyState}>
          {searchTerm ? (
            <p>No shorts found matching "{searchTerm}"</p>
          ) : (
            <>
              <p>No shorts available. Create your first short video!</p>
              <AddShortButton onClick={handleAddNew} />
            </>
          )}
        </div>
      ) : (
        <>
          <div className={styles.shortsContainer}>
            {currentShorts.map((item) => (
              <div key={item.id} className={`${styles.shortItem} ${!item.is_public ? styles.hiddenShort : ''}`}>
                <img
                  src={item.thumbnail_url}
                  alt={item.title}
                  className={styles.shortImage}
                />
                {!item.is_public && <div className={styles.hiddenBadge}>Hidden</div>}
                <PlayButtonComponent />
                <div className={styles.shortInfo}>
                  <h3 className={styles.shortTitle}>{item.title}</h3>
                  <p className={styles.shortStats}>
                    {item.views_count} views • {item.likes_count} likes
                  </p>
                </div>
                <div className={styles.iconsOverlay}>
                  <EditIconComponent id={item.id} onEdit={handleEdit} />
                  <DeleteIconComponent id={item.id} onDelete={confirmDelete} />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this short video? This action cannot be undone."
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default ShortsVideoContent;